import React from 'react';
import {
  ButtonTertiary,
  cssColorValue,
  desktopFinale,
  desktopFinaleBold,
  desktopMesto,
  desktopMestoBold,
  screenMdMin,
  screenSmMax,
  semanticColors,
  spacer12,
  spacer16,
  spacer24,
  spacer32,
} from '@spotify-internal/encore-web';
import styled, { css } from 'styled-components';

import { MastheadFooterData, MastheadLink } from 'lib/sanity/types';
import { isLinkVisible } from 'components/Masthead/shared/utils';
import { FooterTextLink } from 'components/Masthead/Footer/Link';
import { useServerSideConstants } from 'providers/ServerSideConstantsProvider';
import { CcpaIcon } from 'components/Masthead/Footer/CcpaIcon';

const CopyrightPolicies = styled.div`
  order: 0;
  flex-grow: 0;
  display: flex;
  flex-direction: row;
  gap: calc(${spacer24} + ${spacer12});

  @media (max-width: ${screenSmMax}) {
    width: 100%;
    justify-content: space-between;
    gap: ${spacer24};
    order: 1;
    align-self: stretch;

    // Languages with long words spill off the screen
    overflow-x: scroll;
    white-space: nowrap;
  }
`;

const CopyrightWrapper = styled.div`
  flex-shrink: 0;
  color: ${cssColorValue(semanticColors.textSubdued)};
  ${() => desktopFinale};
  @media (min-width: ${screenMdMin}) {
    ${() => desktopMesto}
  }
`;

const PoliciesWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacer16};
  flex-wrap: wrap;
  @media (min-width: ${screenMdMin}) {
    column-gap: ${spacer32};
    row-gap: ${spacer24};
  }
`;

const linkStyles = css`
  ${desktopFinaleBold};
  color: ${cssColorValue(semanticColors.textSubdued)};
  @media (min-width: ${screenMdMin}) {
    ${desktopMestoBold}
  }
`;

const StyledPolicyLink = styled(FooterTextLink)`
  && {
    ${linkStyles}
  }
`;

const StyledPrivacySettingsButton = styled(ButtonTertiary)`
  ${linkStyles}
  min-block-size: auto;
  padding: 0;

  &:active,
  &:hover,
  &:focus {
    transform: none;
  }

  &:hover,
  &:focus {
    color: ${cssColorValue(semanticColors.textBrightAccent)};
  }

  &::after {
    inline-size: 100%;
    bottom: -3px;
  }
`;

const copyrightYear = new Date().getFullYear();

interface Props {
  footerEntry: MastheadFooterData;
}

const PrivacyRegulation = ({ title, privacyRegulation }: { title: string; privacyRegulation: string }) => {
  if (privacyRegulation === 'gdpr') {
    return <StyledPrivacySettingsButton className="optanon-show-settings">{title}</StyledPrivacySettingsButton>;
  }

  if (privacyRegulation === 'ccpa') {
    return (
      <>
        <StyledPrivacySettingsButton className="ot-sdk-show-settings">{title}</StyledPrivacySettingsButton>
        <CcpaIcon />
      </>
    );
  }

  return null;
};

const LinkItem = ({ target, href, title, variant, privacyRegulation }: MastheadLink) => {
  if (variant === 'text') {
    return (
      <StyledPolicyLink href={href} target={target}>
        {title}
      </StyledPolicyLink>
    );
  }

  if (variant === 'privacySetting' && privacyRegulation) {
    return <PrivacyRegulation title={title} privacyRegulation={privacyRegulation} />;
  }

  return null;
};

export const FooterCopyrightPolicies = ({ footerEntry }: Props) => {
  const { isLoggedIn } = useServerSideConstants();

  return (
    <CopyrightPolicies>
      {(footerEntry.corporateLinks || []).map((item, i) => {
        return (
          <PoliciesWrapper key={`${item.label}-${i}`}>
            {item.entries
              .filter(({ link }) => isLinkVisible(link, isLoggedIn))
              .map(entry => (
                <LinkItem key={entry.title} {...entry.link} />
              ))}
          </PoliciesWrapper>
        );
      })}
      <CopyrightWrapper>&#169; {copyrightYear} Spotify AB</CopyrightWrapper>
    </CopyrightPolicies>
  );
};
